// Curated, realistic per-string gauge options for the configurator, so users
// pick from sane values instead of free-typing numbers. Values are inch-decimal
// strings ("0.009") matching ConfiguratorString.gaugeValue and preset data.
//
// Indexed by instrument category → string count → position (0 = highest string).
// Any preset value not already listed is merged in at read time by
// gaugeOptionsFor(), so a chosen preset is always reselectable.

type ByPosition = string[][];

const ELECTRIC_6: ByPosition = [
  ["0.009", "0.010", "0.011", "0.012", "0.013"], // High E
  ["0.010", "0.011", "0.012", "0.013", "0.014", "0.015", "0.016"], // B
  ["0.014", "0.016", "0.017", "0.018", "0.020", "0.022", "0.024"], // G
  ["0.022", "0.024", "0.026", "0.028", "0.030", "0.032"], // D
  ["0.030", "0.032", "0.034", "0.036", "0.038", "0.042"], // A
  ["0.038", "0.042", "0.046", "0.048", "0.050", "0.052", "0.056"], // Low E
];
const ELECTRIC_7: ByPosition = [...ELECTRIC_6, ["0.056", "0.059", "0.060", "0.064", "0.068", "0.070"]]; // Low B
const ELECTRIC_8: ByPosition = [...ELECTRIC_7, ["0.070", "0.074", "0.080", "0.084", "0.090"]]; // Low F#

const BASS_4: ByPosition = [
  ["0.030", "0.035", "0.040", "0.045"], // G
  ["0.050", "0.055", "0.060", "0.065", "0.070"], // D
  ["0.070", "0.075", "0.080", "0.085", "0.090"], // A
  ["0.090", "0.095", "0.100", "0.105", "0.110"], // Low E
];
const BASS_5: ByPosition = [...BASS_4, ["0.120", "0.125", "0.130", "0.135"]]; // Low B
const BASS_6: ByPosition = [
  ["0.025", "0.028", "0.030", "0.032"], // High C
  ...BASS_4, // G D A E
  ["0.120", "0.125", "0.130", "0.135"], // Low B
];

const ACOUSTIC_6: ByPosition = [
  ["0.010", "0.011", "0.012", "0.013"], // E
  ["0.014", "0.015", "0.016", "0.017"], // B
  ["0.024", "0.025", "0.026"], // G (wound)
  ["0.030", "0.032", "0.034", "0.035"], // D
  ["0.038", "0.039", "0.042", "0.045"], // A
  ["0.047", "0.048", "0.052", "0.053", "0.056"], // Low E
];

const CLASSICAL_6: ByPosition = [
  ["0.028", "0.029", "0.030"], // E (nylon)
  ["0.032", "0.033", "0.034"], // B
  ["0.040", "0.041", "0.042"], // G
  ["0.029", "0.030", "0.031"], // D (wound nylon)
  ["0.036", "0.037", "0.039"], // A
  ["0.043", "0.044", "0.045", "0.047"], // Low E
];

const UKULELE_4: ByPosition = [
  ["0.022", "0.024", "0.026"], // A
  ["0.030", "0.032", "0.034"], // E
  ["0.034", "0.036", "0.038"], // C
  ["0.024", "0.026", "0.028"], // G
];

const GAUGE_OPTIONS: Record<string, Record<number, ByPosition>> = {
  electric: { 6: ELECTRIC_6, 7: ELECTRIC_7, 8: ELECTRIC_8 },
  bass: { 4: BASS_4, 5: BASS_5, 6: BASS_6 },
  acoustic: { 6: ACOUSTIC_6 },
  classical: { 6: CLASSICAL_6 },
  ukulele: { 4: UKULELE_4 },
};

// Broad fallback for counts/categories without a curated column (e.g. 12-string).
const FALLBACK: string[] = [
  "0.008", "0.009", "0.010", "0.011", "0.012", "0.013", "0.014", "0.016", "0.017",
  "0.018", "0.020", "0.022", "0.024", "0.026", "0.028", "0.030", "0.032", "0.034",
  "0.036", "0.038", "0.040", "0.042", "0.044", "0.046", "0.048", "0.050", "0.052",
  "0.056", "0.060", "0.065", "0.070", "0.080", "0.090", "0.100", "0.105", "0.110",
  "0.120", "0.125", "0.130",
];

/** Normalize a gauge value to a canonical 3-decimal inch string ("0.009"). */
export function normalizeGauge(value: string | number): string {
  const n = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(n) || n <= 0) return "";
  const inches = n >= 1 ? n / 1000 : n;
  return inches.toFixed(Number.isInteger(Number((inches * 1000).toPrecision(15))) ? 3 : 4);
}

/**
 * Curated gauge options for a string position, always including `ensure`
 * (the current/preset value) so a selection is never orphaned. Sorted thin→thick.
 */
export function gaugeOptionsFor(
  category: string,
  count: number,
  position: number,
  ensure?: string,
): string[] {
  const base = GAUGE_OPTIONS[category]?.[count]?.[position] ?? FALLBACK;
  const set = new Set(base.map(normalizeGauge));
  const ensured = ensure ? normalizeGauge(ensure) : "";
  if (ensured) set.add(ensured);
  return Array.from(set)
    .filter((v) => v.length > 0)
    .sort((a, b) => parseFloat(a) - parseFloat(b));
}
